// @ts-strict-ignore
import React, {
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AggregationEditorContext } from './context';
import AggregationsSubEditorContainer from './AggregationsSubEditorContainer';
import { useAppDispatch, useAppSelector } from '~/store/reduxHooks';
import { selectWorkflowData, selectWorkflowProps } from 'flow/store/redux/selectors';
import { getAggregationConfigForAttribute } from 'flow/store/engine/update/helpers';
import { populateDataRouteData } from 'flow/service/dataRoutes/dataRoutes';
import type { AggregationSourceData } from './types';
import type { DataRoute } from 'flow/store/engine/types';
import {
  aggregationComponentsToSourceData,
  sourceDataToAggregationComponents,
} from './transformers';
import { getIsSaveDisabled } from './helpers';
import { updateDataRouteValue } from 'flow/store/redux/slice';
import { RuleEditorContext } from '../NodeEditorDrawer/context';
import { getMultiSelectProps } from '../NodeEditorDrawer/ThresholdsTab/RuleBuilderContainer/Rule/Threshold/helpers';

interface AggregationDataRouteWrapperProps {
  dataRoute: DataRoute;
}

const AggregationDataRouteWrapper = ({ dataRoute }: AggregationDataRouteWrapperProps) => {
  const dispatch = useAppDispatch();
  const { actions } = useAppSelector(selectWorkflowData);
  const {
    services, aggregationConfig: aggregationConfigByObjectType,
  } = useAppSelector(selectWorkflowProps);
  const { setIsolatedEditingBlock } = useContext(RuleEditorContext);

  const populatedDataRoute = useMemo(
    () => populateDataRouteData({ actions, services, dataRoute }),
    [actions, services, dataRoute],
  );

  const attribute = populatedDataRoute?.attribute?.details;

  const aggregationConfig = useMemo(
    () => (
      attribute
        ? getAggregationConfigForAttribute(attribute, aggregationConfigByObjectType)
        : undefined
    ),
    [attribute, aggregationConfigByObjectType],
  );

  const aggregationEditorContainerRef = useRef<HTMLDivElement>(null);
  const [sourceData, setSourceData] = useState<AggregationSourceData>(
    () => aggregationComponentsToSourceData(dataRoute.aggregationComponents),
  );

  const isSaveDisabled = useMemo(
    () => getIsSaveDisabled(sourceData),
    [sourceData],
  );

  const aggregationEditorContext = useMemo(() => {
    const handleClose = () => {
      setIsolatedEditingBlock({
        originPublicId: undefined,
        originType: undefined,
        originCaretIndex: undefined,
      });
    };

    return {
      sourceData,
      setSourceData,
      handleSave: () => {
        dispatch(updateDataRouteValue({
          path: `${populatedDataRoute.publicId}.aggregationComponents`,
          value: sourceDataToAggregationComponents(
            sourceData, populatedDataRoute.aggregationComponents,
          ),
        }));
        handleClose();
      },
      handleCancel: () => {
        setSourceData(aggregationComponentsToSourceData(
          populatedDataRoute.aggregationComponents,
        ));
        handleClose();
      },
      aggregationEditorContainerRef,
      multiSelectProps: getMultiSelectProps({
        action: populatedDataRoute?.sourceAction,
        options: { isPreview: false },
      }),
      aggregationConfig,
      isSaveDisabled,
    };
  }, [
    sourceData,
    setIsolatedEditingBlock,
    dispatch,
    populatedDataRoute,
    aggregationConfig,
    isSaveDisabled,
  ]);

  if (!aggregationConfig) return null;

  return (
    <AggregationEditorContext.Provider value={aggregationEditorContext}>
      <AggregationsSubEditorContainer />
    </AggregationEditorContext.Provider>
  );
};

export default AggregationDataRouteWrapper;
